/**
 * Extracts YAML-style frontmatter from a markdown string.
 * Frontmatter is a `---` delimited block at the very top of the file.
 * Returns the parsed key/value pairs and the remaining body text.
 *
 * @param {string} text - Raw markdown content
 * @returns {{ meta: Record<string, string>, body: string }}
 */
export function extractFrontmatter(text) {
  const meta = {};
  if (!text.startsWith('---')) return { meta, body: text };

  const end = text.indexOf('\n---', 3);
  if (end === -1) return { meta, body: text };

  const frontmatter = text.slice(3, end).trim();
  const body = text.slice(end + 4).replace(/^\n/, '');

  frontmatter.split('\n').forEach((line) => {
    const colon = line.indexOf(':');
    if (colon === -1) return;
    const key = line.slice(0, colon).trim();
    const value = line.slice(colon + 1).trim();
    if (key) meta[key] = value;
  });

  return { meta, body };
}

/**
 * Parses a markdown string into portfolio panel objects.
 *
 * Supported syntax:
 *   ![alt text](/path/to/image.png)          → { type: 'visual', image, alt }
 *   ![alt text](/path/to/image.png){no-border} → { type: 'visual', image, alt, noBorder: true }
 *   ## Headline                               → starts a text panel
 *   Paragraph text (after a ## heading)       → body of the text panel
 *   [component:name key=value ...]            → { type: 'component', name, props }
 *
 * Component props are parsed as unquoted key=value pairs separated by spaces.
 * Quoted values (key="hello world") are also supported.
 *
 * @param {string} text - Raw markdown content
 * @returns {Array<{type: string, image?: string, alt?: string, noBorder?: string, headline?: string, body?: string, name?: string, props?: object}>}
 */
export function parseMarkdown(text) {
  const panels = [];
  const lines = text.split('\n');

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();

    // Image line: ![alt](/path){no-border}{mobile-only}
    const imageMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)((?:\{no-border\}|\{mobile-only\})*)$/);
    if (imageMatch) {
      const [, alt, image, flags] = imageMatch;
      const panel = { type: 'visual', image, alt };
      if (flags.includes('{no-border}')) panel.noBorder = 'true';
      if (flags.includes('{mobile-only}')) panel.mobileOnly = 'true';
      panels.push(panel);
      i++;
      continue;
    }

    // Component line: [component:name key=value ...]{no-border}{mobile-only}
    const componentMatch = line.match(/^\[component:([^\s\]]+)([^\]]*)\]((?:\{no-border\}|\{mobile-only\})*)$/);
    if (componentMatch) {
      const [, name, propsStr, flags] = componentMatch;
      const props = parseComponentProps(propsStr.trim());
      const panel = { type: 'component', name, props };
      if (flags.includes('{no-border}')) panel.noBorder = true;
      if (flags.includes('{mobile-only}')) panel.mobileOnly = true;
      panels.push(panel);
      i++;
      continue;
    }

    // Title line: # Title
    if (line.startsWith('# ')) {
      panels.push({ type: 'title', text: line.slice(2).trim() });
      i++;
      continue;
    }

    // Heading line: ## Headline
    if (line.startsWith('## ')) {
      const headline = line.slice(3).trim();
      // Collect subsequent non-empty, non-heading, non-image lines as body
      const bodyLines = [];
      i++;
      while (i < lines.length) {
        const next = lines[i].trim();
        if (next === '' || next.startsWith('## ') || next.startsWith('![')) break;
        bodyLines.push(next);
        i++;
      }
      panels.push({ type: 'text', headline, body: bodyLines.join(' ') });
      continue;
    }

    i++;
  }

  return panels;
}

/**
 * Parses a component props string into a key/value object.
 * Supports: key=value and key="quoted value"
 * Example: ' before=/a.png after=/b.png beforeLabel="Before"'
 * → { before: '/a.png', after: '/b.png', beforeLabel: 'Before' }
 *
 * @param {string} str
 * @returns {object}
 */
function parseComponentProps(str) {
  const props = {};
  const re = /([^\s=]+)=(?:"([^"]*)"|([\S]*))/g;
  let m;
  while ((m = re.exec(str)) !== null) {
    const [, key, quoted, unquoted] = m;
    props[key] = quoted !== undefined ? quoted : unquoted;
  }
  return props;
}
